import { useState } from 'react'
import { useCrosswordStore } from '../store'
import { CrosswordViewColumn } from './CrosswordViewColumn'
import { CrosswordWord } from './CrosswordText'
import { PairingGridItem } from './ListItems'
import { getPossiblePairings, getWordsInGroup, getLetters, getLettersFromText } from '../lib/pairings'
import { suggestInflectedForms } from '../lib/constructor'
import { useCrosswordSettings } from '../contexts/CrosswordSettingsContext'
import type { Word } from '../types'

// Size config for add variant button (matches CrosswordText cell sizes)
const buttonSizeConfig: Record<string, string> = {
  '2xs': 'w-2.5 h-2.5 text-[7px]',
  'xs': 'w-3.5 h-3.5 text-[8px]',
  'sm': 'w-4.5 h-4.5 text-[10px]',
  'base': 'w-5.5 h-5.5 text-xs',
  'lg': 'w-7 h-7 text-sm',
  'xl': 'w-8 h-8 text-base',
  '2xl': 'w-9 h-9 text-lg',
  '3xl': 'w-11 h-11 text-xl',
}

export type OverlayMode = 'none' | 'word' | 'pair' | 'word-pair'

type WordVariantSuggestion = {
  text: string
  sourceWordText: string
  // Same-list duplicate of this inflection — can be grouped with selected.
  existingSameListWord: Word | null
  // Other-list duplicate (different role, same letters) — can be marked as twin.
  existingOtherListWord: Word | null
}

type ExploreOverlayProps = {
  mode: OverlayMode
  onClose: () => void
  onModeChange: (mode: OverlayMode) => void
}

export function ExploreOverlay({ mode, onClose, onModeChange }: ExploreOverlayProps) {
  const {
    words,
    pairings,
    selectedWordId,
    selectedPairingId,
    setSelectedWord,
    setSelectedPairing,
    getOrCreatePairing,
    updateWord,
    deleteWord,
    addVariant,
    createWordGroup,
    markAsTwins
  } = useCrosswordStore()

  const { size } = useCrosswordSettings()
  // Word editing state
  const [editingWord, setEditingWord] = useState(false)
  const [editWordText, setEditWordText] = useState('')
  const [addingVariant, setAddingVariant] = useState(false)
  const [variantText, setVariantText] = useState('')

  // Derived state
  const selectedWord = words.find(w => w.id === selectedWordId)
  const selectedPairing = pairings.find(p => p.id === selectedPairingId)
  const groupWords = selectedWord ? getWordsInGroup(selectedWord, words) : []

  // Get possible pairings for selected word
  const possiblePairings = (() => {
    if (!selectedWord || (mode !== 'word' && mode !== 'word-pair')) return []

    const allPossible = groupWords.flatMap(sourceWord =>
      getPossiblePairings(sourceWord, words, pairings).map(item => ({
        ...item,
        sourceWord
      }))
    )

    // Deduplicate by (sourceWord, matchingWord) — different group members produce
    // distinct pairings even when they share opposite-list candidates.
    const seen = new Set<string>()
    const deduped = allPossible.filter(item => {
      const key = `${item.sourceWord.id}:${item.word.id}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })

    // Sort: rejected items last
    return deduped.sort((a, b) => {
      const aRejected = a.pairing?.status === 'rejected' ? 1 : 0
      const bRejected = b.pairing?.status === 'rejected' ? 1 : 0
      return aRejected - bRejected
    })
  })()

  const variantSuggestions = (() => {
    if (!selectedWord || (mode !== 'word' && mode !== 'word-pair')) return [] as WordVariantSuggestion[]

    const selectedGroupLetters = new Set(groupWords.map(word => getLetters(word)))
    // Index by (list, letters) so cross-list duplicates surface as twins, not groups.
    const wordsByListAndLetters = new Map<string, Word>()
    for (const word of words) {
      const key = `${word.list ?? 'unsorted'}:${getLetters(word)}`
      wordsByListAndLetters.set(key, word)
    }

    const selectedListKey = selectedWord.list ?? 'unsorted'
    const otherListKey: string = selectedWord.list === 'A' ? 'B' : selectedWord.list === 'B' ? 'A' : 'unsorted'

    const suggestionsByLetters = new Map<string, WordVariantSuggestion>()
    for (const sourceWord of groupWords) {
      for (const inflectedText of suggestInflectedForms(sourceWord.text)) {
        const letters = getLettersFromText(inflectedText)
        if (selectedGroupLetters.has(letters) || suggestionsByLetters.has(letters)) continue

        const existingSameListWord = wordsByListAndLetters.get(`${selectedListKey}:${letters}`) ?? null
        const existingOtherListWord = otherListKey === selectedListKey
          ? null
          : wordsByListAndLetters.get(`${otherListKey}:${letters}`) ?? null

        suggestionsByLetters.set(letters, {
          text: inflectedText,
          sourceWordText: sourceWord.text,
          existingSameListWord,
          existingOtherListWord
        })
      }
    }

    return Array.from(suggestionsByLetters.values()).sort((a, b) => {
      const lengthDiff = b.text.length - a.text.length
      if (lengthDiff !== 0) return lengthDiff
      return a.text.localeCompare(b.text)
    })
  })()

  const getGroupWordIds = (word: Word): string[] => {
    if (!word.groupId) return [word.id]
    return words.filter(candidate => candidate.groupId === word.groupId).map(candidate => candidate.id)
  }

  const handleGroupVariant = (word: Word) => {
    if (!selectedWord) return
    const mergedIds = new Set<string>(getGroupWordIds(selectedWord))
    for (const wordId of getGroupWordIds(word)) {
      mergedIds.add(wordId)
    }
    if (mergedIds.size >= 2) {
      createWordGroup(Array.from(mergedIds))
    }
  }

  const handleSelectPossiblePairing = (sourceWordId: string, matchingWordId: string) => {
    const sourceWord = words.find(w => w.id === sourceWordId)
    if (!sourceWord) return

    const pairing = getOrCreatePairing(
      sourceWord.list === 'A' ? sourceWord.id : matchingWordId,
      sourceWord.list === 'A' ? matchingWordId : sourceWord.id
    )

    if (pairing) {
      setSelectedPairing(pairing.id)
      // Show both word and pair panels
      onModeChange('word-pair')
    }
  }

  if (mode === 'none') return null

  // Position calculations: w-80 (320px) sidebar + px-4 (16px) padding + gap-3 (12px)
  const leftOffset = 'calc(320px + 16px + 12px)'

  return (
    <>
      {/* Backdrop - only covers the grid area (after sidebar + gap), below header */}
      <div
        className="fixed right-0 bottom-0 bg-gray-100/70 backdrop-blur-sm  z-40"
        style={{
          left: leftOffset,
          top: '60px' // approximate header height
        }}
        onClick={onClose}
      />

      {/* Panel - positioned after the sidebar, below header */}
      <div
        className="fixed bottom-0 z-50 flex gap-3 pb-4"
        style={{
          left: leftOffset,
          top: '60px' // approximate header height
        }}
      >
        {/* Word detail panel - shown in word mode or word-pair mode */}
        {(mode === 'word' || mode === 'word-pair') && selectedWord && (
          <div className="w-80 h-full flex flex-col bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Selected word display */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                  {groupWords.length > 1 ? 'Word Group' : 'Selected Word'}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400 tabular-nums">{getLetters(selectedWord).length} letters</span>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Words list - editable */}
              <div className="space-y-1.5">
                {groupWords.map(word => (
                  <div key={word.id} className="flex items-center gap-2 group min-w-0">
                    {editingWord && editWordText !== undefined && word.id === selectedWord.id ? (
                      <input
                        type="text"
                        value={editWordText}
                        onChange={e => setEditWordText(e.target.value)}
                        onBlur={() => {
                          if (editWordText.trim() && editWordText !== word.text) {
                            updateWord(word.id, { text: editWordText.trim() })
                          }
                          setEditingWord(false)
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            if (editWordText.trim() && editWordText !== word.text) {
                              updateWord(word.id, { text: editWordText.trim() })
                            }
                            setEditingWord(false)
                          }
                          if (e.key === 'Escape') setEditingWord(false)
                        }}
                        className="px-2 py-1 border border-gray-300 rounded text-base font-mono bg-white"
                        autoFocus
                      />
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedWord(word.id)
                          setEditingWord(true)
                          setEditWordText(word.text)
                        }}
                        className={`max-w-full overflow-x-auto hover:opacity-70 transition-opacity ${word.id === selectedWord.id ? '' : 'opacity-50'}`}
                      >
                        <CrosswordWord text={word.text} />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (confirm(`Delete "${word.text}"?`)) {
                          deleteWord(word.id)
                          if (word.id === selectedWord.id) {
                            const remaining = groupWords.filter(w => w.id !== word.id)
                            setSelectedWord(remaining[0]?.id ?? null)
                            if (remaining.length === 0) onClose()
                          }
                        }
                      }}
                      className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all text-sm"
                      title="Delete"
                    >
                      ✕
                    </button>
                  </div>
                ))}

                {/* Add variant button */}
                {addingVariant ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={variantText}
                      onChange={e => setVariantText(e.target.value)}
                      onBlur={() => {
                        if (variantText.trim()) {
                          addVariant(selectedWord.id, variantText.trim())
                        }
                        setVariantText('')
                        setAddingVariant(false)
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && variantText.trim()) {
                          addVariant(selectedWord.id, variantText.trim())
                          setVariantText('')
                          setAddingVariant(false)
                        }
                        if (e.key === 'Escape') { setAddingVariant(false); setVariantText('') }
                      }}
                      placeholder="New variant..."
                      className="px-2 py-1 border border-gray-300 rounded text-base font-mono bg-white"
                      autoFocus
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => setAddingVariant(true)}
                    className={`${buttonSizeConfig[size]} border border-gray-200 border-dashed rounded-[2px] flex items-center justify-center text-gray-300 hover:text-gray-500 hover:border-gray-400 transition-colors`}
                    title="Add variant"
                  >
                    +
                  </button>
                )}

                {variantSuggestions.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-medium uppercase tracking-wider text-gray-400">
                        Suggested Variants
                      </span>
                      <span className="text-[11px] text-gray-400 tabular-nums">
                        {variantSuggestions.length}
                      </span>
                    </div>
                    <div className="space-y-1.5 max-h-32 overflow-y-auto pr-1">
                      {variantSuggestions.map(suggestion => {
                        const sameList = suggestion.existingSameListWord
                        const otherList = suggestion.existingOtherListWord
                        const subtitle = sameList
                          ? `already in List ${sameList.list ?? '—'}`
                          : otherList
                            ? `also in List ${otherList.list ?? '—'}`
                            : `from ${suggestion.sourceWordText}`
                        return (
                          <div
                            key={suggestion.text}
                            className="flex items-center justify-between gap-2 px-2 py-1 rounded-md border border-gray-100 bg-gray-50/40"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-mono leading-none text-gray-700 truncate">
                                {suggestion.text}
                              </p>
                              <p className="text-[11px] text-gray-400 truncate">{subtitle}</p>
                            </div>
                            {sameList ? (
                              <button
                                onClick={() => handleGroupVariant(sameList)}
                                title="Group with selected word"
                                className="px-2 py-1 text-[11px] font-medium rounded transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300"
                              >
                                Group
                              </button>
                            ) : otherList ? (
                              <button
                                onClick={() => markAsTwins(selectedWord.id, otherList.id)}
                                title="Mark as duplicate across lists (excludes both from suggestions when one is placed)"
                                className="px-2 py-1 text-[11px] font-medium rounded transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300"
                              >
                                Mark twin
                              </button>
                            ) : (
                              <button
                                onClick={() => addVariant(selectedWord.id, suggestion.text)}
                                className="px-2 py-1 text-[11px] font-medium rounded bg-gray-800 text-white hover:bg-gray-700 transition-colors"
                              >
                                Add
                              </button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Possible pairs header */}
            <div className="px-3 py-2 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-gray-500">
                {possiblePairings.length} possible pairs
              </span>
            </div>

            {/* Possible pairs list */}
            <div className="flex-1 overflow-y-auto p-2">
              <div className="grid grid-cols-2 gap-1.5 min-w-0">
                {possiblePairings.map(({ word: matchingWord, pairing, sourceWord }) => (
                  <PairingGridItem
                    key={`${sourceWord.id}:${matchingWord.id}`}
                    textA={sourceWord.text}
                    textB={matchingWord.text}
                    autoHighlight
                    selected={pairing ? selectedPairingId === pairing.id : false}
                    pairingStatus={pairing?.status}
                    onClick={() => handleSelectPossiblePairing(sourceWord.id, matchingWord.id)}
                  />
                ))}
                {possiblePairings.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-6 px-2 col-span-2">
                    No matches
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* CrosswordViewColumn - shown in pair mode or word-pair mode */}
        {(mode === 'pair' || mode === 'word-pair') && selectedPairing && (
          <div className="w-80 h-full flex flex-col bg-white rounded-xl shadow-lg overflow-hidden relative">
            <button
              onClick={onClose}
              className="absolute top-3 right-3 z-10 text-gray-400 hover:text-gray-600 transition-colors"
            >
              ✕
            </button>
            <CrosswordViewColumn
              pairing={selectedPairing}
              onShowWord={(wordId) => {
                setSelectedWord(wordId)
                onModeChange('word-pair')
              }}
            />
          </div>
        )}
      </div>
    </>
  )
}
